// ═══════════════════════════════════════════════════════════════
// ClearView CRM Persona Service — Supabase CRUD
// ═══════════════════════════════════════════════════════════════
import { supabase } from "../lib/supabase";
import { debugLog } from "../utils/debugLog";

export const personaService = {
  /**
   * Fetch all personas ordered by sort_order
   */
  async getAll() {
    const { data, error } = await supabase
      .from("personas")
      .select("*")
      .order("sort_order");

    if (error) {
      debugLog.error("personaService.getAll", error);
      throw error;
    }
    debugLog.data("Personas loaded from Supabase", { count: data.length });

    // Convert snake_case DB fields to camelCase for app consistency
    return data.map((p) => ({
      id: p.id,
      name: p.name,
      description: p.description || "",
      color: p.color,
      icon: p.icon || "User",
      actionHint: p.action_hint || "",
      sortOrder: p.sort_order ?? 0,
      createdAt: p.created_at,
    }));
  },

  /**
   * Create a new persona
   */
  async create(personaData) {
    const dbData = {
      id: personaData.id,
      name: personaData.name,
      description: personaData.description || "",
      color: personaData.color || "#6b7280",
      icon: personaData.icon || "User",
      action_hint: personaData.actionHint || "",
      sort_order: personaData.sortOrder ?? 0,
    };

    const { data, error } = await supabase
      .from("personas")
      .insert(dbData)
      .select()
      .single();

    if (error) {
      debugLog.error("personaService.create", error);
      throw error;
    }
    debugLog.action("Persona created", { id: data.id, name: data.name });

    return {
      id: data.id,
      name: data.name,
      description: data.description || "",
      color: data.color,
      icon: data.icon || "User",
      actionHint: data.action_hint || "",
      sortOrder: data.sort_order ?? 0,
      createdAt: data.created_at,
    };
  },

  /**
   * Update an existing persona
   */
  async update(id, updates) {
    const dbUpdates = {};
    if (updates.name !== undefined) dbUpdates.name = updates.name;
    if (updates.description !== undefined)
      dbUpdates.description = updates.description;
    if (updates.color !== undefined) dbUpdates.color = updates.color;
    if (updates.icon !== undefined) dbUpdates.icon = updates.icon;
    if (updates.actionHint !== undefined)
      dbUpdates.action_hint = updates.actionHint;
    if (updates.sortOrder !== undefined)
      dbUpdates.sort_order = updates.sortOrder;

    const { data, error } = await supabase
      .from("personas")
      .update(dbUpdates)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      debugLog.error("personaService.update", error);
      throw error;
    }
    debugLog.action("Persona updated", { id });

    return {
      id: data.id,
      name: data.name,
      description: data.description || "",
      color: data.color,
      icon: data.icon || "User",
      actionHint: data.action_hint || "",
      sortOrder: data.sort_order ?? 0,
      createdAt: data.created_at,
    };
  },

  /**
   * Delete a persona (does NOT cascade to members — they keep stale value)
   */
  async remove(id) {
    const { error } = await supabase.from("personas").delete().eq("id", id);

    if (error) {
      debugLog.error("personaService.remove", error);
      throw error;
    }
    debugLog.action("Persona deleted", { id });
  },
};
